<template>
  <TransitionRoot
    :show="isOpen"
    appear
    as="div"
    class="ui-dialog"
    :style="{ zIndex }"
    data-refid="dialogElement"
  >
    <TransitionChild
      v-if="withBack"
      as="template"
      enter="ease-out duration-300"
      leave="ease-in duration-200"
      enter-from="opacity-0"
      enter-to="opacity-100"
      leave-from="opacity-100"
      leave-to="opacity-0"
    >
      <div class="ui-dialog__back" :style="{ zIndex }" @click="handleClose" />
    </TransitionChild>
    <TransitionChild
      enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
      enter-to="opacity-100 translate-y-0 sm:scale-100"
      leave-from="opacity-100 translate-y-0 sm:scale-100"
      leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
      as="template"
      enter="ease-out duration-300"
      leave="ease-in duration-200"
      appear
    >
      <div
        class="ui-dialog__container"
        :class="containerClasses"
        :style="{ zIndex }"
        v-bind="$attrs"
      >
        <div v-if="isTitleVisible" class="ui-dialog__title" :class="titleClasses">
          <slot name="title">{{ title }}</slot>
        </div>
        <UIButtonIcon
          v-if="!unclosable"
          :leading="XMarkIcon"
          variant="gray"
          fill="light"
          size="sm"
          class="ui-dialog__close"
          @click="handleClose"
        />
        <div class="ui-dialog__content" :class="contentClasses">
          <slot />
        </div>
        <div v-if="$slots.footer" class="ui-dialog__footer" :class="footerClasses">
          <slot name="footer" />
        </div>
      </div>
    </TransitionChild>
  </TransitionRoot>
</template>

<script lang="ts" setup>
import { computed, useSlots } from 'vue'

import { DialogSize } from './utils/types'

import { TransitionChild, TransitionRoot } from '@headlessui/vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { UIButtonIcon } from '@ui/buttons'

type Props = {
  isOpen: boolean
  zIndex: number

  title?: string

  size?: DialogSize

  withBack?: boolean

  unclosable?: boolean

  flat?: boolean
}

type Emits = {
  close: []
}

const { size = 'md', withBack = true, ...props } = defineProps<Props>()
const emit = defineEmits<Emits>()

defineOptions({
  inheritAttrs: false,
})

const slots = useSlots()

const containerClasses = computed(() => ({
  [`ui-dialog__container--${size}`]: true
}))

const titleClasses = computed(() => ({
  ['ui-dialog__title--unclosable']: props.unclosable
}))

const contentClasses = computed(() => ({
  ['ui-dialog__content--flat']: props.flat
}))

const footerClasses = computed(() => ({
  ['ui-dialog__footer--flat']: props.flat
}))

const isTitleVisible = computed(() => !!props.title || slots.title)

const handleClose = () => {
  if (props.unclosable) return
  emit('close')
}
</script>

<style scoped>
@import './styles/colors.css';

.ui-dialog {

  &__back {
    @apply fixed inset-0;
    @apply bg-[rgb(var(--dialog-back)/0.5)];
  }

  &__container {
    @apply max-h-[calc(100vh-6rem)] max-w-[calc(100vw-2rem)];
    @apply fixed top-1/2 left-1/2;
    @apply -translate-x-1/2 -translate-y-1/2;
    @apply flex flex-col;
    @apply bg-body;
    @apply shadow-xl rounded-lg;
    @apply overflow-hidden;

    &--sm {
      @apply w-[24rem];
    }

    &--md {
      @apply w-[28rem];
    }

    &--2md {
      @apply w-[34rem];
    }

    &--3md {
      @apply w-[45rem];
    }

    &--lg {
      @apply w-[72rem];
    }

    &--full {
      @apply w-[calc(100vw-2rem)];
      @apply h-[calc(100vh-2rem)];
    }
  }

  &__title {
    @apply min-h-12;
    @apply flex items-center;
    @apply py-[.555rem] sm:py-1 px-default pr-12;
    @apply border-b border-common-border;
    @apply text-base leading-tight font-medium;

    &--unclosable {
      @apply pr-default;
    }
  }

  &__close {
    @apply absolute top-1 sm:top-2 right-1 sm:right-2;
  }

  &__content {
    @apply py-4 px-default gap-4;
    @apply flex flex-col flex-auto;
    @apply overflow-y-auto;

    &--flat {
      @apply p-0;
    }
  }

  &__footer {
    @apply py-4 px-default;

    &--flat {
      @apply p-0;
    }
  }
}
</style>
